import { useState } from 'react';
import {
  Badge, Button, Code, Group, Modal, NumberInput, Select, Stack, Table, Text,
  TextInput, Tooltip, Alert, SimpleGrid, Card,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import {
  IconBan, IconTrash, IconSearch, IconAlertTriangle, IconActivity, IconBuildingCommunity,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { PageHeader } from '../../components/PageHeader';
import { SectionCard } from '../../components/SectionCard';
import { BlockLoader } from '../../components/BlockLoader';
import { TablePagination } from '../../components/TablePagination';
import { usePagedQuery } from '../../hooks/usePagedQuery';
import { FetchOverlay } from '../../components/FetchOverlay';
import { formatDateTime } from '../../lib/format';
import { uzDateProps } from '../../lib/dateInput';
import { confirmDelete } from '../../lib/confirm';

interface Qator {
  id: string; createdAt: string; tenantId: string | null; tenantName: string | null;
  userId: string | null; login: string | null; role: string | null;
  user: { fullName: string } | null;
  method: string; path: string; status: number; ip: string; ms: number; userAgent: string | null;
}
interface Blok {
  id: string; ip: string; reason: string; until: string | null; auto: boolean; hits: number; createdAt: string;
}
interface Top {
  hisoblar: { userId: string; login: string | null; soni: number }[];
  manzillar: { ip: string; soni: number }[];
  radEtilgan: number;
  jami: number;
}

/** Maktab filtri: `none` — maktabsiz yozuvlar (backend `/superadmin/audit` bilan bir xil kalit). */
const NO_TENANT = 'none';
interface TenantOpt { id: string; name: string; slug: string }

const kunKalit = (d: Date | null) =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '';

/** Status kodiga rang: xatolar ko'zga tashlanib tursin. */
const statusRang = (s: number) => (s >= 500 ? 'red' : s >= 400 ? 'orange' : 'teal');

/**
 * AUDIT — kim, nima, qachon, qaysi IP'dan + IP bloklash.
 *
 * Nega superadminda: jurnalda BARCHA maktablarning amallari bor, ya'ni u
 * tenantlararo ma'lumot. Maktab direktoriga ko'rsatib bo'lmaydi.
 */
export default function Audit() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [ip, setIp] = useState('');
  const [method, setMethod] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);

  // Maktablar ro'yxati — boshqa superadmin sahifalari bilan AYNI so'rov/kesh (SaasPremium, SaasGifts)
  const { data: tenants = [] } = useQuery({
    queryKey: ['sa-tenants-opt'],
    queryFn: async () => (await api.get<TenantOpt[]>('/superadmin/tenants')).data,
    staleTime: 60_000,
  });
  const tenantOptions = [
    { value: NO_TENANT, label: t('audit.noTenant') },
    ...tenants.map((tn) => ({ value: tn.id, label: `${tn.name} (${tn.slug})` })),
  ];
  const tenantLabel = tenantId === NO_TENANT
    ? t('audit.noTenant')
    : tenants.find((tn) => tn.id === tenantId)?.name ?? null;

  const filtrlar = {
    ...(tenantId ? { tenantId } : {}),
    ...(ip.trim() ? { ip: ip.trim() } : {}),
    ...(method ? { method } : {}),
    ...(status ? { status } : {}),
    ...(q.trim() ? { q: q.trim() } : {}),
    ...(from ? { from: kunKalit(from) } : {}),
    ...(to ? { to: kunKalit(to) } : {}),
  };

  const jurnal = usePagedQuery<Qator>(
    `saas-audit:${JSON.stringify(filtrlar)}`,
    '/superadmin/audit',
    filtrlar,
  );

  // Kartalar ham MAKTAB filtriga bo'ysunadi (boshqa filtrlar emas — ular doim "oxirgi 24 soat")
  const { data: top } = useQuery({
    queryKey: ['saas-audit-top', tenantId ?? ''],
    queryFn: async () =>
      (await api.get<Top>('/superadmin/audit/top', { params: tenantId ? { tenantId } : {} })).data,
    refetchInterval: 60_000,
  });

  const { data: bloklar = [], isLoading: bloklarYuklanmoqda } = useQuery({
    queryKey: ['saas-blocked-ips'],
    queryFn: async () => (await api.get<Blok[]>('/superadmin/blocked-ips')).data,
  });

  const [blokModal, setBlokModal] = useState<{ ip: string; hours: number | string; reason: string } | null>(null);

  const xato = (e: unknown) =>
    notifications.show({
      color: 'red',
      message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error'),
    });

  const blokla = useMutation({
    mutationFn: (v: { ip: string; hours?: number; reason: string }) => api.post('/superadmin/blocked-ips', v),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('audit.blocked') });
      setBlokModal(null);
      qc.invalidateQueries({ queryKey: ['saas-blocked-ips'] });
    },
    onError: xato,
  });

  const ochir = useMutation({
    mutationFn: (id: string) => api.delete(`/superadmin/blocked-ips/${id}`),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('audit.unblocked') });
      qc.invalidateQueries({ queryKey: ['saas-blocked-ips'] });
    },
    onError: xato,
  });

  return (
    <>
      <PageHeader title={t('nav.audit')} subtitle={t('audit.pageDesc')} />

      {/* Oxirgi sutkaning qisqacha manzarasi — hujumni qator-qator o'qib emas,
          bir qarashda sezish uchun. */}
      {top && (
        <SimpleGrid cols={{ base: 1, sm: 3 }} mb="md">
          <Card withBorder padding="md">
            <Text size="xs" c="dimmed">{t('audit.last24')}{tenantLabel ? ` · ${tenantLabel}` : ''}</Text>
            <Text fw={700} size="xl">{top.jami}</Text>
            <Text size="xs" c="dimmed">{t('audit.actions')}</Text>
          </Card>
          <Card withBorder padding="md">
            <Text size="xs" c="dimmed">{t('audit.rejected')}</Text>
            <Text fw={700} size="xl" c={top.radEtilgan > 0 ? 'orange' : undefined}>{top.radEtilgan}</Text>
            <Text size="xs" c="dimmed">{t('audit.rejectedHint')}</Text>
          </Card>
          <Card withBorder padding="md">
            <Text size="xs" c="dimmed">{t('audit.topAccount')}</Text>
            <Text fw={700} size="xl">{top.hisoblar[0]?.soni ?? 0}</Text>
            <Text size="xs" c="dimmed">{top.hisoblar[0]?.login ?? '—'}</Text>
          </Card>
        </SimpleGrid>
      )}

      {/* Bloklangan manzillar */}
      <SectionCard title={t('audit.blockedTitle')} icon={IconBan} mb="md">
        <Group justify="space-between" mb="sm">
          <Text size="sm" c="dimmed">{t('audit.blockedHint')}</Text>
          <Button size="compact-sm" variant="light" leftSection={<IconBan size={14} />}
            onClick={() => setBlokModal({ ip: '', hours: 24, reason: '' })}>
            {t('audit.blockAdd')}
          </Button>
        </Group>
        {bloklarYuklanmoqda ? (
          <BlockLoader variant="table" />
        ) : !bloklar.length ? (
          <Text size="sm" c="dimmed">{t('audit.blockedEmpty')}</Text>
        ) : (
          <Table.ScrollContainer minWidth={620}>
            <Table verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('audit.ip')}</Table.Th>
                  <Table.Th>{t('audit.reason')}</Table.Th>
                  <Table.Th>{t('audit.until')}</Table.Th>
                  <Table.Th>{t('audit.hits')}</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {bloklar.map((b) => (
                  <Table.Tr key={b.id}>
                    <Table.Td ff="monospace">{b.ip}</Table.Td>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <Text size="sm">{b.reason}</Text>
                        {b.auto && <Badge size="xs" color="orange" variant="light">{t('audit.auto')}</Badge>}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{b.until ? formatDateTime(b.until) : t('audit.forever')}</Text>
                    </Table.Td>
                    <Table.Td ff="monospace">{b.hits}</Table.Td>
                    <Table.Td>
                      <Button size="compact-xs" variant="subtle" color="red"
                        leftSection={<IconTrash size={13} />}
                        loading={ochir.isPending && ochir.variables === b.id}
                        onClick={() => confirmDelete(b.ip, () => ochir.mutate(b.id))}>
                        {t('audit.unblock')}
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>

      {/* Jurnal */}
      <SectionCard title={t('audit.logTitle')} icon={IconActivity} p={0} pos="relative">
        <FetchOverlay visible={jurnal.fetching && !jurnal.isLoading} />
        <Group p="md" pb="xs" gap="sm" align="flex-end" wrap="wrap">
          <Select label={t('audit.school')} w={220} searchable clearable value={tenantId}
            placeholder={t('audit.allSchools')}
            data={tenantOptions}
            leftSection={<IconBuildingCommunity size={14} />}
            nothingFoundMessage={t('common.noResults')}
            onChange={(v) => { setTenantId(v); jurnal.resetPage(); }}
            comboboxProps={{ withinPortal: true }} />
          <TextInput label={t('audit.ip')} placeholder="1.2.3.4" value={ip} w={150}
            onChange={(e) => { setIp(e.currentTarget.value); jurnal.resetPage(); }} />
          <TextInput label={t('audit.path')} placeholder="/staff" value={q} w={180}
            leftSection={<IconSearch size={14} />}
            onChange={(e) => { setQ(e.currentTarget.value); jurnal.resetPage(); }} />
          <Select label={t('audit.method')} w={130} clearable value={method}
            data={['POST', 'PATCH', 'PUT', 'DELETE']}
            onChange={(v) => { setMethod(v); jurnal.resetPage(); }}
            comboboxProps={{ withinPortal: true }} />
          <Select label={t('audit.status')} w={150} clearable value={status}
            data={[
              { value: '2', label: t('audit.status2') },
              { value: '4', label: t('audit.status4') },
              { value: '5', label: t('audit.status5') },
            ]}
            onChange={(v) => { setStatus(v); jurnal.resetPage(); }}
            comboboxProps={{ withinPortal: true }} />
          <DateInput {...uzDateProps} label={t('audit.from')} value={from} w={140} clearable
            onChange={(v) => { setFrom(v); jurnal.resetPage(); }} />
          <DateInput {...uzDateProps} label={t('audit.to')} value={to} w={140} clearable
            onChange={(v) => { setTo(v); jurnal.resetPage(); }} />
        </Group>

        {jurnal.isLoading ? (
          <BlockLoader variant="table" />
        ) : !jurnal.items.length ? (
          <Text size="sm" c="dimmed" p="md">{t('audit.empty')}</Text>
        ) : (
          <>
            <Table.ScrollContainer minWidth={900}>
              <Table highlightOnHover verticalSpacing="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('audit.when')}</Table.Th>
                    <Table.Th>{t('audit.who')}</Table.Th>
                    <Table.Th>{t('audit.school')}</Table.Th>
                    <Table.Th>{t('audit.what')}</Table.Th>
                    <Table.Th>{t('audit.status')}</Table.Th>
                    <Table.Th>{t('audit.ip')}</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {jurnal.items.map((r) => (
                    <Table.Tr key={r.id}>
                      <Table.Td><Text size="xs" style={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.createdAt)}</Text></Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500}>{r.user?.fullName ?? r.login ?? t('audit.anon')}</Text>
                        {r.login && <Text size="xs" c="dimmed" ff="monospace">{r.login}</Text>}
                      </Table.Td>
                      <Table.Td><Text size="xs">{r.tenantName ?? '—'}</Text></Table.Td>
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <Badge size="xs" variant="light"
                            color={r.method === 'DELETE' ? 'red' : r.method === 'POST' ? 'blue' : 'grape'}>
                            {r.method}
                          </Badge>
                          <Code>{r.path}</Code>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" variant="light" color={statusRang(r.status)}>{r.status}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Tooltip label={r.userAgent ?? '—'} multiline w={280} withArrow>
                          <Text size="xs" ff="monospace">{r.ip}</Text>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        <Button size="compact-xs" variant="subtle" color="red"
                          onClick={() => setBlokModal({ ip: r.ip, hours: 24, reason: `${r.login ?? '?'} — ${r.path}` })}>
                          {t('audit.block')}
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            <TablePagination
              page={jurnal.page} pages={jurnal.pages} total={jurnal.total}
              limit={jurnal.limit} onPage={jurnal.setPage} onLimit={jurnal.setLimit}
            />
          </>
        )}
      </SectionCard>

      <Modal opened={!!blokModal} onClose={() => setBlokModal(null)} title={t('audit.blockAdd')} centered>
        <Stack>
          <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
            {t('audit.blockWarn')}
          </Alert>
          <TextInput label={t('audit.ip')} required value={blokModal?.ip ?? ''}
            onChange={(e) => setBlokModal((v) => v && { ...v, ip: e.currentTarget.value })} />
          <TextInput label={t('audit.reason')} value={blokModal?.reason ?? ''}
            onChange={(e) => setBlokModal((v) => v && { ...v, reason: e.currentTarget.value })} />
          <NumberInput label={t('audit.hours')} description={t('audit.hoursHint')} min={0}
            value={blokModal?.hours ?? 24}
            onChange={(v) => setBlokModal((s) => s && { ...s, hours: v })} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setBlokModal(null)}>{t('common.cancel')}</Button>
            <Button color="red" loading={blokla.isPending}
              disabled={!blokModal?.ip.trim()}
              onClick={() => blokModal && blokla.mutate({
                ip: blokModal.ip.trim(),
                hours: Number(blokModal.hours) || undefined,
                reason: blokModal.reason.trim() || 'Qo\'lda bloklandi',
              })}>
              {t('audit.block')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
